import { useQuery } from "@apollo/client";
import { Box, Spinner } from "grommet";
import { CaretPrevious } from "grommet-icons";
import { useState } from "react";
import { toast } from "react-toastify";
import {
  GET_DENY_WARS,
  GET_DENY_WAR_CONTRIBUTOR,
} from "../../queries/user_queries";
import { DenyWar, UserCount } from "../../types/DenyStatus";
import { getDateDifference } from "../../util/DateUtil";

const DenyWars = () => {
  const [selectedStartDate, setSelectedStartDate] = useState<string | null>(
    null
  );
  const { data } = useQuery(GET_DENY_WARS);
  if (data?.getDenyWars === undefined) {
    return null;
  }

  const wars = data.getDenyWars;

  // list
  const warList = (
    <div className="flex flex-col gap-2">
      {wars.map((w: DenyWar, index: number) => {
        const startDate = new Date(w.startDate);
        const endDate = w.winner === 0 ? new Date() : new Date(w.endDate);

        const color =
          w.winner === 1
            ? "border-error"
            : w.winner === 2
            ? "border-success"
            : "";
        return (
          <div
            className={`btn btn-outline rounded-box p-4 justify-between flex flex-row h-auto overflow-v ${color}`}
            onClick={() => setSelectedStartDate(startDate.toISOString())}
          >
            <div className="self-center text-primary">{w.winAmount}</div>
            <div className="flex gap-2 flex-col">
              <div className="flex flex-row wrap justify-around">
                <div className="text-center text-error">{w.denies}</div>
                <div className="text-center text-success">{w.undenies}</div>
              </div>
              <div className="flex flex-row wrap justify-between">
                <div className="text-center">
                  {startDate.toLocaleDateString() +
                    " " +
                    startDate.toLocaleTimeString()}
                </div>
                <div className="mx-2"> {"=>"}</div>
                <div className="text-center">
                  {endDate.toLocaleDateString() +
                    " " +
                    endDate.toLocaleTimeString()}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
  return (
    <div className="bg-base-200 h-full p-2 overflow-y-auto">
      <div className="text-lg m-2 flex-row flex">
        {selectedStartDate ? (
          <div className="me-2" onClick={() => setSelectedStartDate(null)}>
            <CaretPrevious />
          </div>
        ) : null}
        Deny Wars
      </div>
      {selectedStartDate ? (
        <DenyWarDetails
          startDate={selectedStartDate}
          denyWar={wars.find(
            (w: DenyWar) =>
              new Date(w.startDate).toISOString() === selectedStartDate
          )}
        />
      ) : (
        warList
      )}
    </div>
  );
};

type Props = {
  startDate: string;
  denyWar: DenyWar;
};
const DenyWarDetails = (props: Props) => {
  const { startDate: selectedStartDate, denyWar } = props;
  const { data } = useQuery(GET_DENY_WAR_CONTRIBUTOR, {
    variables: { startDate: selectedStartDate },
  });

  if (!data?.getDenyWarContributors) {
    return <Spinner />;
  }
  const { topDeniers, topUndeniers, progressDeniers, progressUndeniers } =
    data.getDenyWarContributors;

  const deniers = topDeniers.map((d: UserCount) => {
    return (
      <div className="flex flex-row justify-between">
        <div>{d.username}</div>
        <div>{d.count}</div>
      </div>
    );
  });
  const undeniers = topUndeniers.map((d: UserCount) => {
    return (
      <div className="flex flex-row justify-between">
        <div>{d.username}</div>
        <div>{d.count}</div>
      </div>
    );
  });
  const startDate = new Date(denyWar.startDate);
  const endDate = denyWar.endDate ? new Date(denyWar.endDate) : new Date();

  const dateDifference = getDateDifference(startDate, endDate);

  const handleCopy = () => {
    // Use the clipboard API to write text to the clipboard
    const totalUsers: string[] = [
      ...topDeniers,
      ...topUndeniers,
      ...progressDeniers,
      ...progressUndeniers,
    ].map((d: UserCount) => d.username);
    const uniqueUsers = new Set(totalUsers);
    const text = Array.from(uniqueUsers).join(",");

    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success("Copied list");
      })
      .catch((error) => {
        console.error("Failed to copy text: ", error);
      });
  };
  return (
    <Box
      className="flex flex-col wrap gap-2 justify-between mx-2 h-auto"
      animation={"fadeIn"}
    >
      <div className="bg-base-300 p-2 rounded-box border-primary border-2">
        <div className="flex flex-row wrap justify-between">
          <div className="text-center">
            <div className="text-primary">Start</div>

            {startDate.toLocaleDateString() +
              " " +
              startDate.toLocaleTimeString()}
          </div>
          <div className="mx-2"> {"=>"}</div>
          <div className="text-center">
            <div className="text-primary">End</div>
            {endDate.toLocaleDateString() + " " + endDate.toLocaleTimeString()}
          </div>
        </div>
        <div className="text-center">{`${dateDifference.days} days, ${dateDifference.hours} hours, ${dateDifference.minutes} minutes`}</div>
      </div>
      <div className="text-primary">Win Amount: {denyWar.winAmount}</div>
      <div>
        <div className="text-error flex flex-row justify-between">
          <div>Deniers</div> <div>{denyWar.denies}</div>
        </div>
        <div className="divider divider-error m-0" />

        <div className="h-36 rounded-box p-2 bg-error overflow-y-auto">
          {deniers}
        </div>
      </div>
      <div>
        <div className="text-success flex flex-row justify-between">
          <div>Undeniers</div> <div>{denyWar.undenies}</div>
        </div>
        <div className="divider divider-success m-0" />

        <div className="h-36 rounded-box p-2 bg-success overflow-y-auto">
          {undeniers}
        </div>
      </div>
      <div className="collapse collapse-arrow bg-base-200 border-2 border-base">
        <input type="checkbox" name="my-accordion-2" />
        <div className="collapse-title text-xl font-medium">
          Progress Contributors
        </div>
        <div className="collapse-content">
          <div className="bg-base-100 rounded-box mt-4 p-4">
            {progressDeniers.map((d: UserCount) => {
              return (
                <div className="flex flex-row justify-between text-error">
                  <div>{d.username}</div>
                  <div>{d.count.toFixed(1)}</div>
                </div>
              );
            })}
          </div>
          <div className="bg-base-100 rounded-box mt-4 p-4">
            {progressUndeniers.map((d: UserCount) => {
              return (
                <div className="flex flex-row justify-between text-success">
                  <div>{d.username}</div>
                  <div>{d.count.toFixed(1)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="btn btn-primary" onClick={handleCopy}>
        Copy List
      </div>
    </Box>
  );
};

export default DenyWars;
